/**
 * usePagedList — the one list-fetching pattern for this app
 * (docs/FRONTEND-PLAN.md §2.1).
 *
 * Every list endpoint in the API paginates identically: `limit` (≤100) plus an
 * opaque `lastEvaluatedKey` cursor (integration/openapi.yaml). The response
 * envelope differs per resource (`{proofs}`, `{threads}`, `{notifications}`…),
 * so the CALLER adapts it inside `fetchPage`:
 *
 *   const list = usePagedList(
 *     (cursor) =>
 *       forumApi.listThreads(categoryId, { limit: 25, lastEvaluatedKey: cursor })
 *         .then((d) => ({ items: d.threads || [], lastEvaluatedKey: d.lastEvaluatedKey })),
 *     [categoryId]
 *   );
 *
 * Two rules this hook exists to enforce:
 *   1. An API error NEVER leaves a stuck "Loading…" — `loading` always
 *      resolves to false and `error` is set, so the page renders
 *      <ErrorAlert> + a Retry that calls `reload()`.
 *   2. Mutations that only change one row (mark-read, dismiss, edit) patch the
 *      cached row via `mutateItem`/`removeItem` instead of refetching the page.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const NO_DEPS = [];

/**
 * @param {(cursor: string|null) => Promise<{items: any[], lastEvaluatedKey?: string|null, extra?: any}>} fetchPage
 * @param {any[]} deps  Cache key, same contract as a useEffect dependency array:
 *                      when it changes the list resets and refetches page one.
 */
export function usePagedList(fetchPage, deps = NO_DEPS) {
  const [items, setItems] = useState([]);
  const [extra, setExtra] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // `fetchPage` is usually an inline arrow, so its identity changes every
  // render. Keep it in a ref (synced after render, before the load effect
  // below can re-run) so only `deps` decides when a refetch happens.
  const fetchRef = useRef(fetchPage);
  useEffect(() => {
    fetchRef.current = fetchPage;
  });

  // Monotonic run id: a superseded or unmounted request is ignored rather than
  // racing a newer one into state.
  const runIdRef = useRef(0);
  useEffect(
    () => () => {
      runIdRef.current += 1;
    },
    []
  );

  const load = useCallback((fromCursor) => {
    const runId = (runIdRef.current += 1);
    const appending = Boolean(fromCursor);
    if (appending) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setItems([]);
      setExtra(null);
      setCursor(null);
    }
    setError(null);

    Promise.resolve()
      .then(() => fetchRef.current(fromCursor || null))
      .then((page) => {
        if (runId !== runIdRef.current) return;
        const pageItems = (page && page.items) || [];
        setItems((current) => (appending ? [...current, ...pageItems] : pageItems));
        if (page && page.extra !== undefined) setExtra(page.extra);
        setCursor((page && page.lastEvaluatedKey) || null);
      })
      .catch((err) => {
        if (runId !== runIdRef.current) return;
        // The cursor is left untouched so a failed "load more" can be retried.
        setError(err);
      })
      .then(() => {
        if (runId !== runIdRef.current) return;
        setLoading(false);
        setLoadingMore(false);
      });
  }, []);

  useEffect(() => {
    load(null);
    // `deps` is the caller's declared cache key; the linter cannot statically
    // verify a forwarded array, which is exactly the same trade-off useEffect
    // itself makes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const reload = useCallback(() => load(null), [load]);

  const loadMore = useCallback(() => {
    if (!cursor || loading || loadingMore) return;
    load(cursor);
  }, [cursor, loading, loadingMore, load]);

  /**
   * Patch one cached row in place. `patch` is either an object merged onto the
   * row or a function `(item) => partial`.
   */
  const mutateItem = useCallback((id, patch, keyField = 'id') => {
    setItems((current) =>
      current.map((item) =>
        item && item[keyField] === id
          ? { ...item, ...(typeof patch === 'function' ? patch(item) : patch) }
          : item
      )
    );
  }, []);

  /** Drop one cached row in place (delete/dismiss) without refetching. */
  const removeItem = useCallback((id, keyField = 'id') => {
    setItems((current) => current.filter((item) => !item || item[keyField] !== id));
  }, []);

  return useMemo(
    () => ({
      items,
      extra,
      loading,
      loadingMore,
      error,
      hasMore: Boolean(cursor),
      loadMore,
      reload,
      mutateItem,
      removeItem
    }),
    [items, extra, loading, loadingMore, error, cursor, loadMore, reload, mutateItem, removeItem]
  );
}

export default usePagedList;
